import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

import { getMissingCreateRelationPermissionRequirements } from '@/composables/useCreateRelationPermissions'
import { RelationGroupType } from '@/constants/relations'
import { useAuthStore } from '@/stores/auth'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { translationFunctionDefault } from '@/utilities/translations'

interface PrefillMetaData {
  prefillName?: string | undefined
  sourceDocname?: string | undefined
  sourceDoctype?: string | undefined
  sourceEntityName?: string | undefined
  sourceEntityType?: 'organization' | 'person' | undefined
  sourceImage?: string | undefined
  tw_rating?: string | undefined
  tw_responsible_employee?: null | string | undefined
}

interface OpenCreateRelationOverlayOptions {
  initialPersonOrganisationRelationLinkName?: string | undefined
  organizationRelationType?: string | undefined
  prefillMetaData?: PrefillMetaData | undefined
}

export const useCreateRelationSlideInStore = defineStore('createRelationSlideIn', () => {
  const createRelationOverlayState = reactive<{ open: boolean; type: RelationGroupType }>({
    open: false,
    type: RelationGroupType.organizations,
  })
  const organisationRelationType = ref<string | undefined>('')
  const initialPersonOrganisationRelationLinkName = ref<string | undefined>()
  const prefillMetaData = ref<PrefillMetaData | undefined>()

  const openCreateRelationOverlay = (
    type: RelationGroupType = RelationGroupType.organizations,
    options: OpenCreateRelationOverlayOptions = {},
  ): boolean => {
    const authStore = useAuthStore()
    const simpleNotificationStore = useSimpleNotificationStore()
    const tfDefault = translationFunctionDefault()

    const missingRequirements = getMissingCreateRelationPermissionRequirements(authStore, {
      organizationRelationType: options.organizationRelationType,
      relationGroupType: type,
    })

    if (missingRequirements.length > 0) {
      const missingDoctypes = missingRequirements
        .map((requirement) => requirement.doctype)
        .join(', ')

      console.error('[CreateRelation] Missing permissions for relation creation.', {
        missingRequirements,
        organizationRelationType: options.organizationRelationType,
        relationGroupType: type,
      })
      simpleNotificationStore.triggerSimpleNotificationError(
        tfDefault('createRelation.missingPermissionsNotification', {
          requiredDoctypes: missingDoctypes,
        }),
      )

      return false
    }

    createRelationOverlayState.open = true
    createRelationOverlayState.type = type
    organisationRelationType.value = options.organizationRelationType
    initialPersonOrganisationRelationLinkName.value =
      options.initialPersonOrganisationRelationLinkName
    prefillMetaData.value = options.prefillMetaData

    return true
  }

  const resetCreateRelationOverlay = (): void => {
    createRelationOverlayState.open = false
    organisationRelationType.value = ''
    initialPersonOrganisationRelationLinkName.value = undefined
    prefillMetaData.value = undefined
  }

  const closeCreateRelationOverlay = (): void => {
    const alertVisible = !!document.querySelector('.alert-message-overlay')

    if (alertVisible) {
      return
    }
    resetCreateRelationOverlay()
  }

  return {
    closeCreateRelationOverlay,
    createRelationOverlayState,
    initialPersonOrganisationRelationLinkName,
    openCreateRelationOverlay,
    organisationRelationType,
    prefillMetaData,
  }
})

export type { OpenCreateRelationOverlayOptions, PrefillMetaData }
